import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Button } from "../../../components/SystemComponent/Button";
import { Modal } from "../../../components/SystemComponent/Modal";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { MdEdit, MdDelete, MdLink } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const PackageItemLinkManage = () => {
  const { packageId } = useParams();
  const api = useMemo(() => createApi(), []);

  const [allAddons, setAllAddons] = useState<any[]>([]);
  const [addonLinks, setAddonLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [editingLink, setEditingLink] = useState<any>(null);

  const [mode, setMode] = useState<"select" | "create">("select");

  const [selectedAddon, setSelectedAddon] = useState<any>(null);
  const [addonCode, setAddonCode] = useState("");
  const [addonDescription, setAddonDescription] = useState("");

  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);

    const [itemsRes, linksRes] = await Promise.all([
      api.packageItem.getAll(),
      api.packageItemLink.getByPackageId(Number(packageId)),
    ]);

    setAllAddons(itemsRes?.data?.data ?? itemsRes?.data ?? []);
    setAddonLinks(linksRes?.data?.data ?? linksRes?.data ?? []);

    setLoading(false);
  }, [api, packageId]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setEditingLink(null);
    setSelectedAddon(null);
    setAddonCode("");
    setAddonDescription("");
    setAmount("");
    setMode("select");
    setError(null);
  };

  const linkedAddonIds = addonLinks.map((x) => x.packageItemId);

  const submit = async () => {
    let addon = selectedAddon;

    if (mode === "create") {
      if (!addonCode.trim()) {
        setError("Addon code is required");
        return;
      }

      const createRes = await api.packageItem.create({
        code: addonCode.trim(),
        description: addonDescription.trim(),
      });

      addon = { id: createRes?.data.data };

      if (!addon?.id) {
        setError("Addon could not be created");
        return;
      }

      setAllAddons((p) => [...p, addon]);
    }

    if (!addon?.id) {
      setError("Addon is required");
      return;
    }

    if (amount && (!Number.isInteger(Number(amount)) || Number(amount) < 0)) {
      setError("Amount must be positive integer");
      return;
    }

    const payload = {
      packageId: Number(packageId),
      packageItemId: addon.id,
      amount: amount ? Number(amount) : null,
    };

    if (editingLink) {
      await api.packageItemLink.update(editingLink.id, payload);
    } else {
      await api.packageItemLink.create(payload);
    }

    setOpenModal(false);
    reset();
    load();
  };

  const columns = [
    {
      key: "code",
      title: "Addon Code",
      accessor: (r: any) => r.packageItem?.code,
    },
    {
      key: "description",
      title: "Description",
      accessor: (r: any) => r.packageItem?.description,
    },
    {
      key: "amount",
      title: "Amount",
      accessor: (r: any) => r.amount ?? "-",
    },
    {
      key: "actions",
      title: "Actions",
      render: (row: any) => (
        <GroupButton
          items={[
            {
              id: "edit",
              icon: MdEdit,
              color: "#2563EB",
              onClick: () => {
                setEditingLink(row);
                setSelectedAddon(row.packageItem);
                setAmount(row.amount != null ? String(row.amount) : "");
                setMode("select");
                setOpenModal(true);
              },
            },
            {
              id: "delete",
              icon: MdDelete,
              color: "#DC2626",
              onClick: async () => {
                await api.packageItemLink.delete(row.id);
                load();
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 14 }}>
        <Button icon={MdLink} onClick={() => setOpenModal(true)}>
          Add / Link Addon
        </Button>
      </div>

      <Table
        tableKey="addon-links"
        loading={loading}
        columns={columns}
        data={addonLinks}
      />

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <div style={T.title}>
          {editingLink ? "Update Addon" : "Add / Link Addon"}
        </div>

        <div style={T.form}>
          {!editingLink && (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant={mode === "select" ? "primary" : "ghost"}
                  onClick={() => setMode("select")}
                >
                  Select Existing Addon
                </Button>
                <Button
                  variant={mode === "create" ? "primary" : "ghost"}
                  onClick={() => setMode("create")}
                >
                  + New Addon
                </Button>
              </div>

              {mode === "select" && (
                <div style={T.group}>
                  <label style={T.label}>Addon</label>
                  <select
                    style={T.select}
                    value={selectedAddon?.id ?? ""}
                    onChange={(e) =>
                      setSelectedAddon(
                        allAddons.find((x) => x.id === Number(e.target.value))
                      )
                    }
                  >
                    <option value="">Select addon</option>
                    {allAddons
                      .filter((x) => !linkedAddonIds.includes(x.id))
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {mode === "create" && (
                <>
                  <div style={T.group}>
                    <label style={T.label}>Addon Code</label>
                    <input
                      style={T.input}
                      value={addonCode}
                      onChange={(e) => setAddonCode(e.target.value)}
                    />
                  </div>

                  <div style={T.group}>
                    <label style={T.label}>Description</label>
                    <textarea
                      style={T.textarea}
                      value={addonDescription}
                      onChange={(e) => setAddonDescription(e.target.value)}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div style={T.group}>
            <label style={T.label}>Amount</label>
            <input
              style={T.input}
              type="number"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {error && <div style={T.error}>{error}</div>}
        </div>

        <div style={T.actions}>
          <Button variant="ghost" onClick={() => setOpenModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const T = {
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 14,
  },
  group: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    padding: "0 14px",
    fontSize: 14,
    outline: "none",
  },
  textarea: {
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    padding: 14,
    fontSize: 14,
    minHeight: 90,
    resize: "vertical" as const,
    outline: "none",
  },
  select: {
    height: 44,
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
